import { useEffect, useMemo, useState } from "react";
import { Calculator, Plus, Save, Trash2, Truck } from "lucide-react";
import toast from "react-hot-toast";
import { SettingsSectionHeader } from "./SettingsSectionHeader";
import {
  usePartnerFeeBands,
  useSavePartnerFeeBands,
} from "../../hooks/usePartnerFees";
import { useI18n } from "../../i18n";
import type { PartnerFeeBand } from "../../lib/partnerFees";

type BandDraft = {
  min_km: string;
  max_km: string;
  fee_amount: string;
  driver_amount: string;
};

function toDrafts(bands: PartnerFeeBand[]): BandDraft[] {
  return bands.map((band) => ({
    min_km: String(band.min_km),
    max_km: String(band.max_km),
    fee_amount: String(band.fee_amount),
    driver_amount: band.driver_amount == null ? "" : String(band.driver_amount),
  }));
}

/**
 * Partner delivery fee bands — the rates that price deliveries for this
 * partner's linked clients. The client apps and the WhatsApp AI read these
 * same rates (read-only on their side).
 */
export function DeliveryFeesSettingsTab() {
  const { t } = useI18n();
  const bandsQuery = usePartnerFeeBands();
  const save = useSavePartnerFeeBands();
  const [drafts, setDrafts] = useState<BandDraft[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [testKm, setTestKm] = useState(3.5);

  useEffect(() => {
    if (!bandsQuery.data || loaded) return;
    setDrafts(toDrafts(bandsQuery.data));
    setLoaded(true);
  }, [bandsQuery.data, loaded]);

  const onSave = async () => {
    const parsed: PartnerFeeBand[] = drafts.map((draft, index) => {
      const driverRaw = draft.driver_amount.trim();
      return {
        min_km: Number(draft.min_km),
        max_km: Number(draft.max_km),
        fee_amount: Number(draft.fee_amount),
        driver_amount: driverRaw === "" ? null : Number(driverRaw),
        sort_order: index,
      };
    });
    if (parsed.length === 0) {
      toast.error(t("settings.fees.empty"));
      return;
    }
    for (const band of parsed) {
      if (
        !Number.isFinite(band.min_km) ||
        !Number.isFinite(band.max_km) ||
        !Number.isFinite(band.fee_amount)
      ) {
        toast.error(t("settings.fees.errors.invalid"));
        return;
      }
      if (band.max_km <= band.min_km) {
        toast.error(t("settings.fees.errors.range"));
        return;
      }
      if (band.fee_amount <= 0) {
        toast.error(t("settings.fees.errors.fee"));
        return;
      }
      if (band.driver_amount != null) {
        if (!Number.isFinite(band.driver_amount) || band.driver_amount < 0) {
          toast.error(t("settings.fees.errors.driver"));
          return;
        }
        if (band.driver_amount > band.fee_amount) {
          toast.error(t("settings.fees.errors.driverOverFee"));
          return;
        }
      }
    }
    for (let i = 0; i < parsed.length; i += 1) {
      for (let j = i + 1; j < parsed.length; j += 1) {
        const a = parsed[i];
        const b = parsed[j];
        if (a && b && a.min_km < b.max_km && b.min_km < a.max_km) {
          toast.error(t("settings.fees.errors.overlap"));
          return;
        }
      }
    }
    try {
      const saved = await save.mutateAsync(parsed);
      setDrafts(toDrafts(saved));
      toast.success(t("settings.fees.saved"));
    } catch {
      toast.error(t("settings.fees.saveFailed"));
    }
  };

  const calculatorFee = useMemo(() => {
    const parsed = drafts
      .map((draft) => {
        const driverRaw = draft.driver_amount.trim();
        return {
          min_km: Number(draft.min_km),
          max_km: Number(draft.max_km),
          fee_amount: Number(draft.fee_amount),
          driver_amount: driverRaw === "" ? null : Number(driverRaw),
        };
      })
      .filter(
        (band) =>
          Number.isFinite(band.min_km) &&
          Number.isFinite(band.max_km) &&
          Number.isFinite(band.fee_amount) &&
          band.max_km > band.min_km
      );
    const hit = parsed.find((band) => testKm >= band.min_km && testKm < band.max_km);
    if (hit) return hit;
    const covered = parsed.filter((band) => band.max_km <= testKm);
    const top = covered.sort((a, b) => b.max_km - a.max_km)[0];
    return top ?? null;
  }, [drafts, testKm]);

  const inputClass =
    "w-full rounded-xl border border-[var(--border)] bg-transparent px-3 py-2 text-sm font-semibold text-[var(--text-primary)]";

  return (
    <section
      className="menu-panel"
      role="tabpanel"
      aria-label={t("settings.fees.title")}
    >
      <SettingsSectionHeader
        title={t("settings.fees.title")}
        hint={t("settings.fees.hint")}
        icon={Truck}
        actions={
          <>
            <button
              type="button"
              className="btn-secondary min-h-[44px] gap-2 px-4"
              onClick={() =>
                setDrafts((prev) => [
                  ...prev,
                  { min_km: "", max_km: "", fee_amount: "", driver_amount: "" },
                ])
              }
            >
              <Plus className="h-4 w-4" strokeWidth={2.25} />
              {t("settings.fees.add")}
            </button>
            <button
              type="button"
              className="btn-primary min-h-[44px] gap-2 px-4"
              disabled={save.isPending || bandsQuery.isLoading}
              onClick={() => void onSave()}
            >
              <Save className="h-4 w-4" strokeWidth={2.25} />
              {save.isPending ? t("common.loading") : t("settings.fees.save")}
            </button>
          </>
        }
      />
      <div className="menu-panel__body space-y-5">
        {bandsQuery.isLoading ? (
          <p className="text-sm font-semibold text-[var(--text-muted)]">
            {t("common.loading")}
          </p>
        ) : drafts.length === 0 ? (
          <p className="text-sm font-semibold text-[var(--text-muted)]">
            {t("settings.fees.empty")}
          </p>
        ) : (
          <div className="space-y-3">
            {drafts.map((band, index) => (
              <div
                key={index}
                className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] px-4 py-3"
              >
                <label className="block w-28 space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    {t("settings.fees.minKm")}
                  </span>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={band.min_km}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((draft, i) =>
                          i === index ? { ...draft, min_km: e.target.value } : draft
                        )
                      )
                    }
                  />
                </label>
                <label className="block w-28 space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    {t("settings.fees.maxKm")}
                  </span>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={band.max_km}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((draft, i) =>
                          i === index ? { ...draft, max_km: e.target.value } : draft
                        )
                      )
                    }
                  />
                </label>
                <label className="block w-32 space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    {t("settings.fees.fee")}
                  </span>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={band.fee_amount}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((draft, i) =>
                          i === index
                            ? { ...draft, fee_amount: e.target.value }
                            : draft
                        )
                      )
                    }
                  />
                </label>
                <label className="block w-32 space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    {t("settings.fees.driverAmount")}
                  </span>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    placeholder="—"
                    value={band.driver_amount}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((draft, i) =>
                          i === index
                            ? { ...draft, driver_amount: e.target.value }
                            : draft
                        )
                      )
                    }
                  />
                </label>
                <div className="block w-28 space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    {t("settings.fees.margin")}
                  </span>
                  <p className="px-1 py-2 text-sm font-bold text-[var(--text-primary)]">
                    {band.driver_amount.trim() === "" ||
                    !Number.isFinite(Number(band.driver_amount))
                      ? "—"
                      : String(
                          Number(band.fee_amount) -
                            Number(band.driver_amount)
                        )}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-secondary ml-auto min-h-[44px] gap-2 px-3"
                  aria-label={t("settings.fees.remove")}
                  onClick={() =>
                    setDrafts((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  <Trash2 className="h-4 w-4" strokeWidth={2.25} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3 rounded-xl border border-[var(--border)] px-4 py-3">
          <div className="settings-block__head">
            <span className="settings-block__icon" aria-hidden>
              <Calculator className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div>
              <p className="settings-block__title">{t("settings.fees.calculator")}</p>
              <p className="settings-block__sub">{t("settings.fees.calculatorHint")}</p>
            </div>
          </div>
          <label className="block space-y-2" htmlFor="partner-fee-slider">
            <span className="text-sm font-bold text-[var(--text-primary)]">
              {t("settings.fees.testDistance").replace(
                "{distance}",
                `${testKm.toFixed(1)} km`
              )}
            </span>
            <input
              id="partner-fee-slider"
              type="range"
              min={0}
              max={15}
              step={0.1}
              value={testKm}
              className="w-full"
              onChange={(e) => setTestKm(Number(e.target.value))}
            />
          </label>
          <p className="text-sm font-bold text-[var(--text-primary)]">
            {calculatorFee == null
              ? t("settings.fees.noBand")
              : t("settings.fees.result").replace(
                  "{fee}",
                  String(calculatorFee.fee_amount)
                )}
          </p>
          {calculatorFee?.driver_amount != null ? (
            <p className="text-sm font-bold text-[var(--text-muted)]">
              {t("settings.fees.driverAmount")}: {calculatorFee.driver_amount} ·{" "}
              {t("settings.fees.margin")}:{" "}
              {calculatorFee.fee_amount - calculatorFee.driver_amount}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
